document.addEventListener('DOMContentLoaded', async () => {
  const form = document.querySelector('.collection-filters__car-select .collection-filters__form');
  if (!form) return;

  const FIELD_ORDER = ['brand', 'model', 'version', 'generation', 'bodytype'];
  const animationDelay = 300;
  const FIELD_INDEX = FIELD_ORDER.reduce((acc, field, index) => {
    acc[field] = index;
    return acc;
  }, {});
    // Brand priority
    const PRIORITY_BRANDS = ['Audi', 'BMW', 'Mercedes-Benz', 'Porsche', 'Volkswagen'].map(brand => brand.toLowerCase());

    const getBrandRank = label => {
      const index = PRIORITY_BRANDS.indexOf(label.trim().toLowerCase());
      return index === -1 ? PRIORITY_BRANDS.length : index;
    };
  const selects = {
    brand: form.querySelector('#car-select-form-brand'),
    model: form.querySelector('#car-select-form-model'),
    version: form.querySelector('#car-select-form-version'),
    generation: form.querySelector('#car-select-form-generation'),
    bodytype: form.querySelector('#car-select-form-body-type')
  };

  // Whether we're on the search results page rather than a real collection
  // page — read once from the same data attribute custom-collection-view.js
  // uses (data-page-type on #custom-collection-app, set in Liquid from
  // request.page_type). On search, picking a vehicle must narrow the
  // CURRENT search instead of navigating to /collections/... — see
  // handleVehicleFilterSelection below.
  const isSearchPage = document.getElementById('custom-collection-app')?.dataset.pageType === 'search';

// --- REGEX PATTERNS ---------------------------------------------------------

const patterns = {
  klasa: /^(\w+)\s+Klasa$/i,                      // A Klasa
  seriaNumber: /^Seria\s+(\d)$/i,                 // Seria 1
  seriaNumberM: /^Seria\s+(\dM)$/i,               // Seria 1M
  seriaMNumber: /^Seria\s+(M\d)$/i,               // Seria M5
  seriaGT: /^Seria\s+(\d\s+GT)$/i,                // Seria 6 GT
  seriaGranCoupe: /^Seria\s+(\d)\s+Gran Coupe$/i, // Seria 2 Gran Coupe
};

// --- SHARED HELPERS ----------------------------------------------------------

const stripSeriaPrefix = (_captures, original) => original.replace("Seria ", "");
const extractGenerationStartYear = (label) => {
  const match = label.match(/\[(\d{4})/);
  return match ? Number(match[1]) : Infinity; // brak roku w etykiecie ląduje na końcu
};

// --- TRANSLATION RULES ------------------------------------------------------

const translations = {
  de: {
    klasa: ([model]) => `${model} Klasse`,
    seriaNumber: ([num]) => `${num}er`,
    seriaNumberM: stripSeriaPrefix,
    seriaMNumber: stripSeriaPrefix,
    seriaGT: stripSeriaPrefix,
    seriaGranCoupe: ([num]) => `${num}er Gran Coupe`,
  },

  en: {
    klasa: ([model]) => `${model} Class`,
    seriaNumber: ([num]) => `Series ${num}`,
    seriaNumberM: stripSeriaPrefix,
    seriaMNumber: stripSeriaPrefix,
    seriaGT: stripSeriaPrefix,
    seriaGranCoupe: ([num]) => `Series ${num} Gran Coupe`,
  },

  fr: {
    klasa: ([model]) => `${model} Classe`,
    seriaNumber: ([num]) => `Série ${num}`,
    seriaNumberM: stripSeriaPrefix,
    seriaMNumber: stripSeriaPrefix,
    seriaGT: stripSeriaPrefix,
    seriaGranCoupe: ([num]) => `Série ${num} Gran Coupe`,
  },
};

// --- CORE TRANSLATION ENGINE ------------------------------------------------

const translate = (input, lang) => {
  const original = input.trim();

  for (const [key, regex] of Object.entries(patterns)) {
    const match = original.match(regex);
    if (!match) continue;

    const rule = translations[lang]?.[key];
    if (!rule) continue;

    const captures = match.slice(1);
    return rule(captures, original);
  }

  return original;
};

const translateFunction = (str) => {
  const lang = document.documentElement.lang || "pl";
  return translate(str, lang);
};


  if (!selects.brand || !selects.model || !selects.version || !selects.generation || !selects.bodytype) return;

  const getPlaceholderLabel = select => select.querySelector('option')?.textContent?.trim() || '';

  const placeholders = {
    brand: getPlaceholderLabel(selects.brand),
    model: getPlaceholderLabel(selects.model),
    version: getPlaceholderLabel(selects.version),
    generation: getPlaceholderLabel(selects.generation),
    bodytype: getPlaceholderLabel(selects.bodytype)
  };

  // ─────────────────────────────────────────────────────────────────
  // Vehicle taxonomy loading.
  //
  // Previously this waited on a global `window.MaxtonVehicleData` (and a
  // `window.MaxtonVehicleDataPromise` set up elsewhere in the theme) being
  // populated before this script ran. If that global wasn't generated in
  // time — or at all — the filters silently broke.
  //
  // This now fetches the taxonomy straight from the JSON file in `assets`,
  // so it no longer depends on any other script/snippet running first or
  // in a particular order.
  //
  // The asset URL is fingerprinted by Shopify and can only be resolved
  // correctly via Liquid, so it's read off a data attribute on the form.
  // Add this in the .liquid file that renders the form:
  //
  //   <form class="collection-filters__form"
  //         data-vehicle-data-url="{{ 'MaxtonVehicleData.json' | asset_url }}">
  //
  // The fetch result is cached on `window` so that if this script somehow
  // runs more than once on the same page, the JSON is only downloaded once.
  // ─────────────────────────────────────────────────────────────────

  const VEHICLE_DATA_URL = form.dataset.vehicleDataUrl;

  const loadVehicleData = () => {
    // Prefer data another part of the theme may have already loaded (see
    // the homepage hero car-select script, which sets these two) — skips
    // a duplicate download when it's already sitting in memory.
    if (window.MaxtonVehicleData && Array.isArray(window.MaxtonVehicleData.nodes)) {
      return Promise.resolve(window.MaxtonVehicleData);
    }

    const fetchDirectly = () => {
      if (!VEHICLE_DATA_URL) {
        console.error('[collection-filters-car-select] Missing data-vehicle-data-url attribute on the filter form.');
        return Promise.resolve(null);
      }

      return fetch(VEHICLE_DATA_URL, { credentials: 'same-origin' })
        .then(response => {
          if (!response.ok) {
            throw new Error(`Request failed with status ${response.status}`);
          }
          return response.json();
        })
        .catch(error => {
          console.error('[collection-filters-car-select] Failed to load vehicle taxonomy.', error);
          return null;
        });
    };

    if (window.MaxtonVehicleDataPromise) {
      return window.MaxtonVehicleDataPromise
        .then(data => (data && Array.isArray(data.nodes)) ? data : fetchDirectly())
        .catch(() => fetchDirectly());
    }

    return fetchDirectly();
  };

  window.__maxtonVehicleDataPromise = window.__maxtonVehicleDataPromise || loadVehicleData();

  const globalData = await window.__maxtonVehicleDataPromise;

  if (!globalData || !Array.isArray(globalData.nodes)) {
    return;
  }

  const dataManager = {
    prepareData(rawData) {
      const nodes = (rawData.nodes ?? [])
        .map((node) => ({
          id: Number(node.id),
          parentId: Number(node.parentId),
          type: node.type ?? '',
          label: node.label ?? '',
          link: node.link ?? ''
        }))
        // node.hide is read from the RAW source node (not the mapped one
        // above, since `hide` isn't carried over into the mapped shape) —
        // a hidden node is dropped entirely here, so it never ends up in
        // nodesById/childrenByParent and can't appear in any select or be
        // reached while walking a chain.
        .filter((node, index) => Number.isFinite(node.id) && Number.isFinite(node.parentId) && !rawData.nodes[index]?.hide);

      const nodesById = new Map();
      const childrenByParent = new Map();

      nodes.forEach((node) => {
        nodesById.set(node.id, node);

        if (!childrenByParent.has(node.parentId)) {
          childrenByParent.set(node.parentId, []);
        }
        childrenByParent.get(node.parentId).push(node);
      });

      childrenByParent.forEach((children) => {
        children.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
      });

      const brandsRootId = 1876;

      return {
        rootId: Number(rawData.rootId) || brandsRootId,
        nodes,
        nodesById,
        childrenByParent
      };
    },
    formatGenerationOptions(nodes) {
      const sorted = [...nodes].sort((a, b) => {
        const yearDiff = extractGenerationStartYear(a.label) - extractGenerationStartYear(b.label);
        if (yearDiff !== 0) return yearDiff;
        return a.label.localeCompare(b.label, undefined, { numeric: true });
      });
      return sorted.map(node => ({ id: node.id, label: node.label, node }));
    },

    formatOptions(nodes) {
      const sorted = [...nodes].sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
      return sorted.map(node => ({ id: node.id, label: node.label, node }));
    },
    formatBrandOptions(nodes) {
      const sorted = [...nodes].sort((a, b) =>
        getBrandRank(a.label) - getBrandRank(b.label)
        || a.label.localeCompare(b.label, undefined, { numeric: true }));
      return sorted.map(node => ({ id: node.id, label: node.label, node }));
    },

    getOptionsForSelect(fieldType, selectionNodes, carData) {
      if (!carData) return [];

      if (fieldType === 'brand') {
        const children = carData.childrenByParent.get(carData.rootId) ?? [];
        return this.formatBrandOptions(children);
      }

      if (!(fieldType in FIELD_INDEX)) return [];

      if (fieldType === 'bodytype') {
        return this.getBodyTypeOptions(selectionNodes, carData);
      }

      const previousField = FIELD_ORDER[FIELD_INDEX[fieldType] - 1];
      const parentNode = selectionNodes?.[previousField];
      if (!parentNode) return [];

      const children = carData.childrenByParent.get(parentNode.id) ?? [];
      const typed = children.filter(child => child.type === fieldType);
      const nodesToFormat = typed.length > 0 ? typed : children;

      if (fieldType === 'generation') {
        return this.formatGenerationOptions(nodesToFormat);
      }

      return this.formatOptions(nodesToFormat);
    },

    getBodyTypeOptions(selectionNodes, carData) {
      const generationNode = selectionNodes?.generation;
      if (generationNode) {
        const children = carData.childrenByParent.get(generationNode.id) ?? [];
        return children.length > 0 ? this.expandBodyTypeOptions(children, carData) : [];
      }

      const versionNode = selectionNodes?.version;
      if (!versionNode) return [];

      const versionChildren = carData.childrenByParent.get(versionNode.id) ?? [];

      if (versionChildren.some(child => child.type === 'generation')) return [];

      return versionChildren.length > 0 ? this.expandBodyTypeOptions(versionChildren, carData) : [];
    },

    expandBodyTypeOptions(nodes, carData) {
      const options = [];

      nodes.forEach((node) => {
        const childNodes = carData.childrenByParent.get(node.id) ?? [];

        if (childNodes.length === 0) {
          options.push({ id: node.id, label: node.label, node });
          return;
        }

        childNodes.forEach(child => {
          options.push({
            id: child.id,
            label: `${node.label} - ${child.label}`,
            node: child
          });
        });
      });

      options.sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));

      return options;
    }
  };

  const carData = dataManager.prepareData(globalData);
  const selectionNodes = {};

  const selectHasOptionValue = (select, value) => {
    if (!select) return false;
    return [...select.options].some(option => option.value === value);
  };

  // ─────────────────────────────────────────────────────────────────
  // Locale-prefix support.
  //
  // The vehicle dataset's `link` field (used as the pushState slug) never
  // includes a locale segment — it's always plain "/collections/xxx". On a
  // store with locale-prefixed URLs (e.g. "/pl/collections/xxx",
  // "/fr/collections/xxx"), pushing that bare slug as the new URL wipes out
  // the "/pl"/"/fr" prefix that was there before.
  //
  // Fix: split the CURRENT url on "/collections/" to get whatever prefix
  // precedes it (the locale, or "" if there isn't one), and split the new
  // `slug` the same way to get just its handle — then rejoin prefix +
  // "/collections/" + new handle. Only the part after "/collections/"
  // ever changes; the locale prefix is carried over untouched.
  // ─────────────────────────────────────────────────────────────────

  const buildLocalizedSlug = (slug) => {
    const [localePrefix] = window.location.pathname.split('/collections/');
    const [, handlePart] = slug.split('/collections/');
    return `${localePrefix}/collections/${handlePart ?? slug.replace(/^\//, '')}`;
  };
    // Set builder link: visible only for a final car (node without children)
    const SET_BUILDER_VIEW = 'set-builder';
    const setBuilderLink = form.closest('.collection-filters__car-select')?.querySelector('[data-set-builder-link]');
  
    const getDeepestSelectedNode = () => FIELD_ORDER.map(fieldType => selectionNodes[fieldType]).filter(Boolean).at(-1) ?? null;
  
    const buildSetBuilderUrl = (link) => {
      const localeRoot = (window.Shopify?.routes?.root ?? '/').replace(/\/$/, '');
      const [, handle] = link.split('/collections/');
      return `${localeRoot}/collections/${handle}?view=${SET_BUILDER_VIEW}`;
    };
  
    const updateSetBuilderLink = () => {
      if (!setBuilderLink) return;
      const node = getDeepestSelectedNode();
      const isFinalVehicle = Boolean(node?.link) && !(carData.childrenByParent.get(node.id)?.length);
  
      setBuilderLink.hidden = !isFinalVehicle;
      setBuilderLink.classList.toggle('hidden', !isFinalVehicle);
      setBuilderLink.classList.remove('show');
      if (!isFinalVehicle) return;
  
      setBuilderLink.href = buildSetBuilderUrl(node.link);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setBuilderLink.classList.add('show'));
      });
    };

  // ─────────────────────────────────────────────────────────────────
  // Initial-selection-from-URL support.
  //
  // Fixes: landing directly on a collection page (typed URL, shared link,
  // bookmark) doesn't go through the filter UI, so nothing gets written to
  // maxtondesign_saved_vehicles / localStorage — there's nothing to restore
  // FROM. This works independently of that storage: it matches the current
  // page's URL directly against the vehicle tree (MaxtonVehicleData) and
  // derives the correct selection from there, every time.
  //
  // A collection handle is a single path segment (e.g. "/collections/alfa-romeo"
  // is a single node, even though the slug itself contains a hyphen) — so this
  // never tries to infer depth by counting hyphens, only by matching the whole
  // link string against the dataset and then walking parentId up to the root.
  //
  // Skipped entirely on the search results page — there's no vehicle
  // collection URL to match there, so this would either no-op or (worse)
  // false-positive match against an unrelated /collections/... path.
  // ─────────────────────────────────────────────────────────────────

  const getNodeDepth = node => {
    if (!node) return -1;

    const visited = new Set();
    let depth = 0;
    let current = node;

    while (current && current.parentId !== carData.rootId) {
      if (visited.has(current.id)) return -1;
      visited.add(current.id);

      current = carData.nodesById.get(current.parentId);
      if (!current) return -1;
      depth += 1;
    }

    return current?.parentId === carData.rootId ? depth + 1 : -1;
  };

  const getNodeChainToRoot = node => {
    const chain = [];
    const visited = new Set();
    let current = node;

    while (current) {
      if (visited.has(current.id)) return null;
      visited.add(current.id);

      chain.unshift(current);

      if (current.parentId === carData.rootId) break;
      current = carData.nodesById.get(current.parentId);
    }

    if (chain.length === 0) return null;
    if (chain[0]?.parentId !== carData.rootId) return null;
    return chain;
  };

  // If multiple nodes somehow share the same link, prefer the deepest one.
  const findBestNodeByLink = link => {
    const target = String(link ?? '').trim();
    if (!target) return null;

    let bestNode = null;
    let bestDepth = -1;

    carData.nodes.forEach(node => {
      const nodeLink = node?.link?.trim();
      if (!nodeLink || nodeLink !== target) return;

      const depth = getNodeDepth(node);
      if (depth > bestDepth) {
        bestDepth = depth;
        bestNode = node;
      }
    });

    return bestNode;
  };

  const applyValue = (fieldType, id) => {
    const select = selects[fieldType];
    if (!select || id == null) return false;
    const value = String(id);
    if (!selectHasOptionValue(select, value)) return false;
    select.value = value;
    return true;
  };

  const prefillFromCurrentUrl = () => {
    if (isSearchPage) return;

    const currentPath = window.location.pathname;

    // Exact match first — normal case given the link format in the dataset
    // (e.g. "/collections/bmw-seria-m3").
    let bestNode = findBestNodeByLink(currentPath);

    // Fallback for locale-prefixed stores (e.g. "/en/collections/bmw") or a
    // trailing slash — match on the last two path segments instead.
    if (!bestNode) {
      const normalizedCurrent = currentPath.replace(/\/$/, '');
      const currentTail = normalizedCurrent.split('/').slice(-2).join('/');
      bestNode = carData.nodes.find(node => {
        const nodeLink = (node?.link ?? '').replace(/\/$/, '');
        return nodeLink.split('/').slice(-2).join('/') === currentTail;
      }) ?? null;
    }

    if (!bestNode) {
      console.warn('[collection-filters-car-select] no matching node for current URL:', currentPath);
      return;
    }

    const chain = getNodeChainToRoot(bestNode);
    if (!chain) return;

    // Walk the chain root-first, applying each ancestor by its OWN `type` —
    // not by its position in the chain — so this correctly handles hierarchies
    // that skip levels or vary in order (e.g. brand -> model -> generation,
    // no version in between).
    chain.forEach((node, index) => {
      const fieldType = node.type;
      if (!FIELD_ORDER.includes(fieldType)) return;
      if (fieldType === 'generation') {
        console.log('timeData - Filters ->', node, 'TIME:', Date.now());
      }
      // Populate this level's options BEFORE selecting a value — options
      // depend on selectionNodes as set by the previous iteration.
      const options = dataManager.getOptionsForSelect(fieldType, selectionNodes, carData);
      if (options.length > 0) {
        const delay = animationDelay * index;
        populateSelect(selects[fieldType], placeholders[fieldType], options);
        selects[fieldType].disabled = false;
        const wrapper = selects[fieldType].parentNode;
        wrapper.classList.remove('hidden');

        setTimeout(() => {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              wrapper.classList.add('show');
            });
          });
        }, delay);
      }

      selectionNodes[fieldType] = node;
      applyValue(fieldType, node.id);
    });

    // Unlock (without selecting) whatever comes after the deepest node
    // actually present in the chain — e.g. show bodytype options after
    // landing on a generation page, same as after a manual change.
    const deepestIndex = Math.max(
      ...chain.map(n => FIELD_ORDER.indexOf(n.type)).filter(i => i !== -1)
    );
    if (deepestIndex !== -1) {
      updateAvailabilityAfterSelection(FIELD_ORDER[deepestIndex]);
    }
    updateSetBuilderLink();
  };

  const resetSelect = (select, placeholder) => {
    select.innerHTML = '';
    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.selected = true;
    placeholderOption.textContent = placeholder;
    select.appendChild(placeholderOption);

    select.disabled = true;
    select.required = false;
    select.parentNode.classList.add('hidden');
  };

  const populateSelect = (select, placeholder, options) => {
    select.innerHTML = '';

    const placeholderOption = document.createElement('option');
    placeholderOption.value = '';
    placeholderOption.selected = true;
    placeholderOption.textContent = placeholder;
    select.appendChild(placeholderOption);

    options.forEach((opt) => {
      if(opt.node.type == "model") {
        opt.label = translateFunction(opt.label)
      }
      const option = document.createElement('option');
      option.value = String(opt.id);
      option.textContent = opt.label;
      select.appendChild(option);
    });
    select.parentNode.classList.remove('show');
    select.parentNode.classList.remove('hidden');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        select.parentNode.classList.add('show');
      });
    });
  };

  const clearDownstream = fromFieldType => {
    const fromIndex = FIELD_ORDER.indexOf(fromFieldType);
    if (fromIndex === -1) return;

    FIELD_ORDER.slice(fromIndex + 1).forEach(fieldType => {
      delete selectionNodes[fieldType];
      resetSelect(selects[fieldType], placeholders[fieldType]);
    });
  };

  const updateAvailabilityAfterSelection = fromFieldType => {
    const selectedIndex = FIELD_ORDER.indexOf(fromFieldType);
    if (selectedIndex === -1) return;

    for (let i = selectedIndex + 1; i < FIELD_ORDER.length; i++) {
      const fieldType = FIELD_ORDER[i];
      const options = dataManager.getOptionsForSelect(fieldType, selectionNodes, carData);

      resetSelect(selects[fieldType], placeholders[fieldType]);

      if (options.length > 0) {
        populateSelect(selects[fieldType], placeholders[fieldType], options);
        selects[fieldType].disabled = false;
      }
    }
  };

  const setSelectionFromSelect = fieldType => {
    const select = selects[fieldType];
    if (!select) return;

    const id = Number(select.value);
    if (!Number.isFinite(id)) {
      delete selectionNodes[fieldType];
      return;
    }

    const node = carData.nodesById.get(id);
    if (!node) {
      delete selectionNodes[fieldType];
      return;
    }

    selectionNodes[fieldType] = node;
  };

  const init = () => {
    const brandOptions = dataManager.getOptionsForSelect('brand', selectionNodes, carData);
    populateSelect(selects.brand, placeholders.brand, brandOptions);
    selects.brand.disabled = false;


    resetSelect(selects.model, placeholders.model);
    resetSelect(selects.version, placeholders.version);
    resetSelect(selects.generation, placeholders.generation);
    resetSelect(selects.bodytype, placeholders.bodytype);

    // After the baseline empty state above, try to prefill from the current
    // URL — this overwrites the relevant selects if a match is found, and
    // leaves everything untouched (still empty) if not (e.g. on a non-vehicle
    // page, or the homepage). No-ops on the search page — see
    // prefillFromCurrentUrl's isSearchPage check.
    prefillFromCurrentUrl();
  };

  function handleVehicleFilterSelection(slug, carDataToSave, carName, finalId) {
    console.log('[collection-filters-car-select] vehicle selected:', {
      slug,
      handleId: finalId,
      carData: carDataToSave,
      carName
    });

    // On the search results page, a vehicle pick narrows the CURRENT
    // search (custom-collection-view.js reads handleId as vehicleNodeId
    // when in search mode) — it must NOT push a /collections/... URL,
    // which would silently leave the search results/URL behind.
    if (isSearchPage) {
      document.dispatchEvent(new CustomEvent('vehicle-filter-changed', {
        detail: { slug, handleId: finalId, carData: carDataToSave, carName }
      }));
      return;
    }

    // Re-attach whatever locale prefix ("/pl", "/fr", ...) the current URL
    // has, since `slug` (the dataset's `link` field) never includes one —
    // without this, pushState below would silently drop the active locale.
    const fullSlug = buildLocalizedSlug(slug);

    // Preserve whatever query string is already on the URL (e.g.
    // ?filter.collection=front-splitter set by the category checkboxes) —
    // `slug` here is only the bare collection pathname, so pushing it alone
    // would silently wipe out those params. We carry them over as-is; if a
    // category filter no longer matches any section for the new vehicle, the
    // category-filter script's own logic already handles that gracefully
    // (nothing matches -> falls back to showing everything checked next
    // time there's no valid match).
    const currentSearch = window.location.search;
    const newUrl = currentSearch ? `${fullSlug}${currentSearch}` : fullSlug;

    // Push the new URL without reloading the page.
    // `slug` here is whatever the dataset's `link` field contains for the
    // deepest currently selected node — adjust if you need to strip a
    // domain/prefix to get just the collection handle.
    history.pushState({ slug, handleId: finalId, carData: carDataToSave, carName }, '', newUrl);

    // Let the rest of the page (e.g. the collection product/category view)
    // know the vehicle filter changed, so it can react without this file
    // needing to know anything about how products are rendered.
    document.dispatchEvent(new CustomEvent('vehicle-filter-changed', {
      detail: { slug, handleId: finalId, carData: carDataToSave, carName }
    }));
  }

  // Reads whatever is currently selected across all levels, and — if the
  // deepest selected node has a link — reports it via handleVehicleFilterSelection.
  // Called after EVERY select's change event, not just on a final submit,
  // since this is a live filter, not a search form.
  const applyCurrentSelection = () => {
    updateSetBuilderLink();
    const brand = selectionNodes?.brand?.label ?? '';
    const model = selectionNodes?.model?.label ?? '';
    const version = selectionNodes?.version?.label ?? '';
    const generation = selectionNodes?.generation?.label ?? '';
    const bodytype = selectionNodes?.bodytype?.label ?? '';

    const carDataToSave = { brand, model, version, generation, bodytype };

    const carNameParts = [brand, model].filter(Boolean);
    if (version) carNameParts.push(version);
    if (generation) carNameParts.push(generation);
    const carName = carNameParts.join(' ');

    const finalNode = selectionNodes.bodytype
      || selectionNodes.generation
      || selectionNodes.version
      || selectionNodes.model
      || selectionNodes.brand;

    const finalId = finalNode.id;
    const slug = finalNode?.link?.trim();

    if (!slug && !finalId) {
      // Nothing meaningful selected yet (e.g. all cleared) — don't push a URL change.
      return;
    }

    handleVehicleFilterSelection(slug, carDataToSave, carName, finalId);
  };

  selects.brand.addEventListener('change', () => {
    setSelectionFromSelect('brand');
    clearDownstream('brand');
    updateAvailabilityAfterSelection('brand');
    applyCurrentSelection();
  });

  selects.model.addEventListener('change', () => {
    setSelectionFromSelect('model');
    clearDownstream('model');
    updateAvailabilityAfterSelection('model');
    applyCurrentSelection();
  });

  selects.version.addEventListener('change', () => {
    setSelectionFromSelect('version');
    clearDownstream('version');
    updateAvailabilityAfterSelection('version');
    applyCurrentSelection();
  });

  selects.generation.addEventListener('change', () => {
    setSelectionFromSelect('generation');
    clearDownstream('generation');
    updateAvailabilityAfterSelection('generation');
    applyCurrentSelection();
  });

  selects.bodytype.addEventListener('change', () => {
    setSelectionFromSelect('bodytype');
    applyCurrentSelection();
  });
  document.addEventListener('refine-selection-requested', () => {
    const nextField = FIELD_ORDER.find(fieldType => {
      const select = selects[fieldType];
      return (
        select &&
        !select.disabled &&
        !select.parentNode.classList.contains('hidden') &&
        !select.value
      );
    });

    if (!nextField) return;

    const target = selects[nextField];
    target.scrollIntoView({ behavior: 'smooth', block: 'center' });
    target.focus();
    target.click();
  });

  // NOTE: material filtering (previously a <select multiple id="car-select-
  // form-material">) now lives entirely in custom-collection-view.js as
  // checkboxes sharing the category filter's markup — this file no longer
  // touches it at all.

  init();
});
document.addEventListener('click', (e) => {
  const toggle = e.target.closest('.collection-filters__car-select .multi-select__toggle');

  if (toggle) {
    const multiSelect = toggle.closest('.multi-select');
    multiSelect.classList.toggle('active');
    return;
  }

  document.querySelectorAll('.multi-select.active').forEach((el) => {
    if (!el.contains(e.target)) el.classList.remove('active');
  });
});